import router from '@/router';
import i18n from '@/plugins/vue-i18n';
import { useAuthStore } from '@/stores/auth.store';
import { usePusherStore, type PusherEventHandler } from '@/stores/pusher.store';
import { useOrgStore } from '@/stores/organisations.store';
import { ShieldExclamationIcon, ShieldCheckIcon } from '@heroicons/vue/24/outline';
import { EntityType, RoleEvent, RoleMessageData } from '@/types/plugins/pusher.types';
import { selectedOrgId } from '@/common/utils/role.utils';
import { usePermissionStore } from '@/stores/permission.store';
import { useRouteHistoryStore } from '@/stores/utils/route-history.store';
import { isRouteBlockedByAbility } from '@/common/utils/route-permission.utils';
import { userIdentity } from '@/common/utils/user-identity';
import type { IconWrapperProps, TagColor } from '@boqnet/ui';

const { t } = i18n.global;

export const REFRESHABLE_ROUTE_NAMES = [
  'orders',
  'claims',
  'accounts',
  'accounts-payable',
  'variations',
  'client-variations',
  'dashboard',
];

/**
 * Joins one of the app-wide channels App.vue listens on and binds its handlers, once. pusher-js
 * keeps a channel and its bindings through a reconnect, and the pusher store keeps its channels
 * through an organisation switch, so binding on every `connected` would stack another handler each
 * time. Only App.vue joins these channels, so one the store already holds is one this has bound.
 */
export const joinAppChannel = (
  channelName: string,
  handlers: Record<string, PusherEventHandler>,
): void => {
  const pusherStore = usePusherStore();
  if (pusherStore.subscribedChannels[channelName]) return;
  if (!pusherStore.subscribeToChannel(channelName)) return;

  for (const [eventName, handler] of Object.entries(handlers)) {
    pusherStore.bindEvent(channelName, eventName, handler);
  }
};

/**
 * Rehydrates permissions and the org list after a role event, returning the org the user was in
 * beforehand.
 */
export const refreshAccessAfterRoleEvent = async (): Promise<string | undefined> => {
  const authStore = useAuthStore();

  // Context: the org-list refresh re-resolves the active org as soon as the removed one leaves the
  // list, so whether the user was sitting in it has to be read first (PLE-1298).
  const activeOrgIdBeforeRefresh = authStore.loggedInUser?.selectedOrg?.id;

  await authStore.getUserPermissions();
  await useOrgStore().getCurrentUserOrgs();

  return activeOrgIdBeforeRefresh;
};

// --- REMOVED Event Handlers ---
export const handleOrgRoleRemoved = async (
  data: RoleMessageData,
  activeOrgIdBeforeRefresh: string | undefined,
) => {
  const authStore = useAuthStore();
  const pusherStore = usePusherStore();

  // Self-initiated removals don't notify the actor.
  if (data.UserId === data.ActionedById) {
    return;
  }

  if (activeOrgIdBeforeRefresh === data.OrgId) {
    if (authStore.loggedInUser.organisations.length > 0) {
      pusherStore.showRoleEventModal(true);
    } else {
      pusherStore.showRoleEventModal(true);
      await router.push({ name: 'no-user-orgs' });
      setTimeout(() => {
        pusherStore.showRoleEventModal(false);
      }, 3000);
    }
  }
};

export const handleProjectRoleRemoved = async (data: RoleMessageData) => {
  const permissionStore = usePermissionStore();
  const orgStore = useOrgStore();
  const pusherStore = usePusherStore();

  if (userIdentity.isBareOrgMember()) {
    pusherStore.showRoleEventModal(true);
    await router.push({
      name: userIdentity.worksOnBoqDatabaseOnly() ? 'boq-admin-vendors' : 'no-projects-or-sites',
      params: { organisationId: selectedOrgId.value },
    });
  }

  if (REFRESHABLE_ROUTE_NAMES.includes(router.currentRoute.value.name as string)) {
    pusherStore.showRoleEventModal(true);
  }

  if (userIdentity.seesProjectNavigation()) {
    await orgStore.getOrgProjects(false, { id: selectedOrgId.value, archived: false });
  }

  if (data.EntityId === router.currentRoute.value.params.id && !userIdentity.isBareOrgMember()) {
    pusherStore.showRoleEventModal(true);
    await router.push({ name: 'projects', params: { organisationId: selectedOrgId.value } });
  }

  if (permissionStore.activeProjectId === data.EntityId) {
    permissionStore.setActiveProject('');
  }
};

export const handleSiteRoleRemoved = async (data: RoleMessageData) => {
  const orgStore = useOrgStore();
  const permissionStore = usePermissionStore();
  const pusherStore = usePusherStore();

  if (userIdentity.isBareOrgMember()) {
    pusherStore.showRoleEventModal(true);
    await router.push({
      name: userIdentity.worksOnBoqDatabaseOnly() ? 'boq-admin-vendors' : 'no-projects-or-sites',
      params: { organisationId: selectedOrgId.value },
    });
  }

  if (REFRESHABLE_ROUTE_NAMES.includes(router.currentRoute.value.name as string)) {
    pusherStore.showRoleEventModal(true);
  }

  if (userIdentity.seesProjectNavigation()) {
    await orgStore.getOrgProjects(false, { id: selectedOrgId.value, archived: false });
  }

  if (
    data.EntityId === router.currentRoute.value.params.siteId &&
    !userIdentity.isBareOrgMember()
  ) {
    pusherStore.showRoleEventModal(true);
    await router.push({ name: 'projects', params: { organisationId: selectedOrgId.value } });
  }

  if (permissionStore.activeSiteId === data.EntityId) {
    permissionStore.setActiveSite('');
  }
};

// --- CHANGED Event Handlers ---
export const handleOrgRoleChanged = async (data: RoleMessageData) => {
  const orgStore = useOrgStore();
  const pusherStore = usePusherStore();

  if (selectedOrgId.value === data.EntityId) {
    if (userIdentity.seesProjectNavigation()) {
      await orgStore.getOrgProjects(false, { id: selectedOrgId.value, archived: false });
      if (router.currentRoute.value.name === 'no-projects-or-sites') {
        await router.push({ name: 'projects', params: { organisationId: selectedOrgId.value } });
      }
    }

    pusherStore.showRoleEventModal(true);
  }
};

export const handleProjectRoleChanged = async (data: RoleMessageData) => {
  const pusherStore = usePusherStore();

  if (data.EntityId === router.currentRoute.value.params.id && data.UserId !== data.ActionedById) {
    pusherStore.showRoleEventModal(true);
  }
};

export const handleSiteRoleChanged = async (data: RoleMessageData) => {
  const pusherStore = usePusherStore();

  if (
    data.EntityId === router.currentRoute.value.params.siteId &&
    data.UserId !== data.ActionedById
  ) {
    pusherStore.showRoleEventModal(true);
  }
};

// --- ADDED Event Handlers ---
export const handleOrgRoleAdded = async (data: RoleMessageData) => {
  const orgStore = useOrgStore();
  const pusherStore = usePusherStore();

  // Self-initiated grants don't notify the actor.
  if (data.UserId === data.ActionedById) {
    return;
  }

  if (selectedOrgId.value === data.EntityId) {
    if (userIdentity.seesProjectNavigation()) {
      await orgStore.getOrgProjects(false, { id: selectedOrgId.value, archived: false });

      if (router.currentRoute.value.name === 'no-projects-or-sites') {
        await router.push({ name: 'projects', params: { organisationId: selectedOrgId.value } });
      }
    }

    pusherStore.showRoleEventModal(true);
  }
};

export const handleProjectRoleAdded = async (data: RoleMessageData) => {
  const orgStore = useOrgStore();
  const pusherStore = usePusherStore();

  if (userIdentity.seesProjectNavigation()) {
    await orgStore.getOrgProjects(false, { id: selectedOrgId.value, archived: false });
  }

  if (
    router.currentRoute.value.name === 'no-projects-or-sites' &&
    userIdentity.seesProjectNavigation()
  ) {
    await router.push({ name: 'projects', params: { organisationId: selectedOrgId.value } });
  }

  if (data.EntityId === router.currentRoute.value.params.id && data.UserId !== data.ActionedById) {
    pusherStore.showRoleEventModal(true);
  }
};

export const handleSiteRoleAdded = async (data: RoleMessageData) => {
  const orgStore = useOrgStore();
  const pusherStore = usePusherStore();

  if (userIdentity.seesProjectNavigation()) {
    await orgStore.getOrgProjects(false, { id: selectedOrgId.value, archived: false });
  }

  if (
    router.currentRoute.value.name === 'no-projects-or-sites' &&
    userIdentity.seesProjectNavigation()
  ) {
    await router.push({ name: 'projects', params: { organisationId: selectedOrgId.value } });
  }

  if (
    data.EntityId === router.currentRoute.value.params.siteId &&
    data.UserId !== data.ActionedById
  ) {
    pusherStore.showRoleEventModal(true);
  }
};

type RoleEventDialogDetails = {
  title: string;
  description: string;
  colour: TagColor & IconWrapperProps['color'];
  icon: string;
};

export const getRoleEventDialogDetails = (
  eventType: RoleEvent,
  entityType: EntityType,
  retainsEntityAccess = false,
): RoleEventDialogDetails => {
  switch (eventType) {
    case 'Removed': {
      // A single org-role removal leaves the user as an org member (default ORG_MEMB),
      // so they retain access — surface a neutral "a role was removed" message instead of
      // the access-lost + switch-org flow, which is only correct for actual org removal.
      if (retainsEntityAccess) {
        return {
          title: t('users.roleEvents.roleRemoved.title'),
          description: t('users.roleEvents.roleRemoved.description'),
          colour: 'blue',
          icon: ShieldCheckIcon as unknown as string,
        };
      }

      return {
        title: t('users.roleEvents.remove.title', { entityType: entityType.toLowerCase() }),
        description: t('users.roleEvents.remove.description'),
        colour: 'red',
        icon: ShieldExclamationIcon as unknown as string,
      };
    }
    case 'Changed': {
      return {
        title: t('users.roleEvents.update.title', { entityType: entityType.toLowerCase() }),
        description: t('users.roleEvents.update.description'),
        colour: 'blue',
        icon: ShieldCheckIcon as unknown as string,
      };
    }
    case 'Added': {
      return {
        title: t('users.roleEvents.add.title', { entityType: entityType.toLowerCase() }),
        description: t('users.roleEvents.add.description'),
        colour: 'green',
        icon: ShieldCheckIcon as unknown as string,
      };
    }
    default: {
      return {
        title: entityType,
        description: '',
        colour: 'gray',
        icon: ShieldCheckIcon as unknown as string,
      };
    }
  }
};

/**
 * Re-evaluate the current route against freshly-rehydrated abilities after a role change.
 *
 * The router guard only runs on navigation, so a live permission change (Pusher) leaves the
 * user sitting on a now-forbidden page — or stranded on `no-permissions` after regaining
 * access — until they refresh or navigate. Call this when the role-event dialog is dismissed:
 *  - current route now forbidden → redirect to `no-permissions`
 *  - on `no-permissions` but access regained → return to the previous route (route-history),
 *    or the default `projects` landing, and let the guard validate it
 *
 * No-op until permissions are initialised (parity with the guard, which only enforces then).
 */
export const revalidateRouteAfterRoleChange = async () => {
  const permissionStore = usePermissionStore();

  if (!permissionStore.isInitialized) {
    return;
  }

  const current = router.currentRoute.value;
  const orgParams = selectedOrgId.value ? { organisationId: selectedOrgId.value } : {};

  if (current.name === 'no-permissions') {
    const previous = useRouteHistoryStore().previousRoute;

    await router.push(
      previous && previous !== current.fullPath
        ? previous
        : { name: 'projects', params: orgParams },
    );
    return;
  }

  if (isRouteBlockedByAbility(current.meta)) {
    await router.push({ name: 'no-permissions', params: orgParams });
  }
};
